export interface DeviceIncidentResolutionCommand {
  incidentId: string;
  expectedUpdatedAt: string;
  note: string;
  idempotencyKey: string;
}

export interface DeviceIncidentResolutionRecovery {
  ownerIdentityKey: string;
  command: DeviceIncidentResolutionCommand;
  state: "CLAIMED" | "OUTCOME_UNKNOWN";
}

export type DeviceIncidentResolutionRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: DeviceIncidentResolutionRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

export const DEVICE_INCIDENT_RESOLUTION_RECOVERY_STORAGE_KEY =
  "billiard-erp:device-incident-resolution:v1";

let pending: DeviceIncidentResolutionRecovery | null = null;
let dispatching: DeviceIncidentResolutionRecovery | null = null;
let hydrated = false;
let unavailable = false;

export function deviceIncidentResolutionIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function bounded(value: unknown, minimum: number, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    [...value].length >= minimum &&
    [...value].length <= maximum
  );
}

function validIdentityKey(value: unknown): value is string {
  if (!bounded(value, 1, 1_024)) return false;
  try {
    const parts = JSON.parse(value) as unknown;
    return (
      Array.isArray(parts) && parts.length === 3 && parts.every((part) => bounded(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function validCommand(value: unknown): value is DeviceIncidentResolutionCommand {
  if (
    !record(value) ||
    !exactKeys(value, ["incidentId", "expectedUpdatedAt", "note", "idempotencyKey"]) ||
    !bounded(value.incidentId, 1, 128) ||
    !bounded(value.note, 2, 120) ||
    !bounded(value.idempotencyKey, 8, 128) ||
    !bounded(value.expectedUpdatedAt, 1, 40)
  ) {
    return false;
  }
  const parsed = Date.parse(value.expectedUpdatedAt);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value.expectedUpdatedAt;
}

function frozen(
  ownerIdentityKey: string,
  command: DeviceIncidentResolutionCommand,
  state: DeviceIncidentResolutionRecovery["state"],
): DeviceIncidentResolutionRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function parsed(value: unknown): DeviceIncidentResolutionRecovery | null {
  if (
    !record(value) ||
    !exactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !record(value.recovery)
  ) {
    return null;
  }
  const candidate = value.recovery;
  if (
    !exactKeys(candidate, ["ownerIdentityKey", "command", "state"]) ||
    !validIdentityKey(candidate.ownerIdentityKey) ||
    !validCommand(candidate.command) ||
    (candidate.state !== "CLAIMED" && candidate.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return frozen(candidate.ownerIdentityKey, candidate.command, candidate.state);
}

function same(
  left: DeviceIncidentResolutionRecovery,
  right: DeviceIncidentResolutionRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function readStored(target: Storage): DeviceIncidentResolutionRecovery | null {
  const encoded = target.getItem(DEVICE_INCIDENT_RESOLUTION_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : parsed(JSON.parse(encoded) as unknown);
}

function save(recovery: DeviceIncidentResolutionRecovery): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    target.setItem(
      DEVICE_INCIDENT_RESOLUTION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStored(target);
    return stored !== null && same(stored, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const target = storage();
  if (!target) {
    unavailable = true;
    return;
  }
  try {
    const encoded = target.getItem(DEVICE_INCIDENT_RESOLUTION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = readStored(target);
    if (!stored) {
      unavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? frozen(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !save(recovery)) {
      unavailable = true;
      return;
    }
    pending = recovery;
  } catch {
    unavailable = true;
  }
}

export function readDeviceIncidentResolutionRecovery(
  identityKey: string,
): DeviceIncidentResolutionRecoveryView {
  hydrate();
  if (unavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  if (!pending) return { status: "NONE" };
  return pending.ownerIdentityKey === identityKey
    ? { status: "OWNED", recovery: pending }
    : { status: "OTHER_OPERATOR" };
}

export function claimDeviceIncidentResolutionRecovery(
  identityKey: string,
  command: DeviceIncidentResolutionCommand,
): DeviceIncidentResolutionRecoveryView {
  const current = readDeviceIncidentResolutionRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!validIdentityKey(identityKey) || !validCommand(command)) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = frozen(identityKey, command, "CLAIMED");
  if (!save(recovery)) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pending = recovery;
  return { status: "OWNED", recovery };
}

function exactDurable(recovery: DeviceIncidentResolutionRecovery, identityKey: string): boolean {
  hydrate();
  if (
    unavailable ||
    pending !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    (typeof window !== "undefined" && !storage())
  ) {
    unavailable = true;
    return false;
  }
  if (typeof window === "undefined") return true;
  try {
    const stored = readStored(storage() as Storage);
    if (stored && same(stored, recovery)) return true;
  } catch {
    // The caller must stop before sending when the recovery evidence is not durable.
  }
  unavailable = true;
  return false;
}

export function beginDeviceIncidentResolutionDispatch(
  recovery: DeviceIncidentResolutionRecovery,
  identityKey: string,
): boolean {
  if (dispatching || !exactDurable(recovery, identityKey)) return false;
  dispatching = recovery;
  return true;
}

export function finishDeviceIncidentResolutionDispatch(
  recovery: DeviceIncidentResolutionRecovery,
  identityKey: string,
): boolean {
  if (dispatching !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatching = null;
  return true;
}

export function markDeviceIncidentResolutionOutcomeUnknown(
  recovery: DeviceIncidentResolutionRecovery,
  identityKey: string,
): boolean {
  if (!exactDurable(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : frozen(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!save(unknown)) {
    unavailable = true;
    return false;
  }
  pending = unknown;
  return true;
}

export function clearDeviceIncidentResolutionRecovery(
  recovery: DeviceIncidentResolutionRecovery,
  identityKey: string,
): boolean {
  if (!exactDurable(recovery, identityKey)) return false;
  if (typeof window !== "undefined") {
    const target = storage();
    if (!target) {
      unavailable = true;
      return false;
    }
    try {
      target.removeItem(DEVICE_INCIDENT_RESOLUTION_RECOVERY_STORAGE_KEY);
      if (target.getItem(DEVICE_INCIDENT_RESOLUTION_RECOVERY_STORAGE_KEY) !== null) {
        unavailable = true;
        return false;
      }
    } catch {
      unavailable = true;
      return false;
    }
  }
  pending = null;
  return true;
}

export function resetDeviceIncidentResolutionRecoveryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
  try {
    storage()?.removeItem(DEVICE_INCIDENT_RESOLUTION_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup resets the in-memory fail-closed state as well.
  }
}

export function resetDeviceIncidentResolutionRecoveryMemoryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
}
